import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import RightRail from "./RightRail";
import GlobalSearch from "./GlobalSearch";
import RunningTimers from "./RunningTimers";
import StartDayPrompt from "./StartDayPrompt";
import { Mail, LayoutGrid, MessageSquare, LogOut, Ticket, Building2, BarChart3, UserCircle, Plus, Layers, BookUser, ChevronDown, Settings, Check, Sun, Moon, Sunrise, BookOpen, Activity, StickyNote, Home, Menu, Plug, Users, ShieldCheck, KeyRound, Lock, FileText, Receipt, Package, Repeat, HelpCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { useCompany } from "../context/CompanyContext";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Avatar } from "./Avatar";
import { GuidedTour } from "./GuidedTour";
import { InstallAppButton } from "./InstallAppButton";

const PRIMARY = [
  { to: "/", label: "Accueil", icon: Home, testid: "nav-home", end: true, mod: null },
  { to: "/mail", label: "Boîte mail", icon: Mail, badge: "unread_emails", testid: "nav-mail", mod: "mail" },
  { to: "/tickets", label: "Tickets", icon: Ticket, badge: "unread_tickets", testid: "nav-tickets", mod: "tickets" },
  { to: "/tasks", label: "Tâches", icon: LayoutGrid, badge: "pending_validations", testid: "nav-tasks", mod: "tasks" },
  { to: "/chat", label: "Chat", icon: MessageSquare, badge: "unread_messages", testid: "nav-chat", mod: "chat" },
  { to: "/suivi", label: "Suivi", icon: Activity, testid: "nav-suivi", mod: "suivi" },
];
const BILLING = [
  { to: "/catalogue", label: "Catalogue", icon: Package, testid: "nav-catalogue", mod: "catalogue", company: true },
  { to: "/devis", label: "Devis", icon: FileText, testid: "nav-devis", mod: "devis", company: true },
  { to: "/factures", label: "Factures", icon: Receipt, testid: "nav-factures", mod: "factures", company: true },
  { to: "/abonnements", label: "Abonnements & MRR", icon: Repeat, testid: "nav-abonnements", mod: "abonnements", company: true },
];
const SECONDARY = [
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", mod: "clients" },
  { to: "/contacts", label: "Répertoire", icon: BookUser, testid: "nav-contacts", mod: "contacts" },
  { to: "/vault", label: "Coffre Accès", icon: KeyRound, testid: "nav-vault", mod: "vault" },
  { to: "/notes", label: "Notes", icon: StickyNote, testid: "nav-notes", mod: "notes" },
  { to: "/kb", label: "Base de connaissances", icon: BookOpen, testid: "nav-kb", mod: "kb" },
  { to: "/integration", label: "Intégration", icon: Plug, testid: "nav-integration", mod: "integration" },
  { to: "/reports", label: "Rapports", icon: BarChart3, testid: "nav-reports", mod: "reports" },
  { to: "/members", label: "Membres & rôles", icon: ShieldCheck, testid: "nav-members", mod: "members" },
  { to: "/companies", label: "Entreprises", icon: Building2, testid: "nav-companies", mod: null },
];

const COMPANY_COLORS = ["#002FA7", "#E60000", "#10B981", "#F59E0B", "#7C3AED", "#0891B2", "#DB2777", "#65A30D"];
function colorFor(id) { let h = 0; for (let i = 0; i < (id || "").length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0; return COMPANY_COLORS[h % COMPANY_COLORS.length]; }
function initials(name) { return (name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?"; }

function useOutside(onClose) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);
  return ref;
}

function CompanySwitcher({ collapsed }) {
  const { managed, selectedId, select } = useCompany();
  const { perCompany } = useRealtime();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useOutside(() => setOpen(false));
  const current = selectedId === "all" ? null : managed.find((c) => c.id === selectedId);
  const allPending = Object.values(perCompany || {}).reduce((a, c) => a + (c.total || 0), 0);

  const Logo = ({ co, size = 30 }) => (
    co?.logo_url
      ? <img src={co.logo_url} alt="" className="rounded-full object-cover shrink-0 ring-1 ring-white/15" style={{ width: size, height: size }} />
      : <div className="rounded-full flex items-center justify-center text-white font-cabinet font-bold shrink-0 ring-1 ring-white/15" style={{ width: size, height: size, background: co ? colorFor(co.id) : "#27272A", fontSize: size * 0.38 }}>{co ? initials(co.name) : <Layers size={size * 0.5} />}</div>
  );

  return (
    <div ref={ref} className="relative">
      <button data-testid="company-switcher" onClick={() => setOpen((o) => !o)} title={current ? current.name : "Toutes les entreprises"}
        className={`flex items-center gap-2.5 bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 transition-colors ${collapsed ? "justify-center rounded-full w-11 h-11 mx-auto p-0.5" : "rounded-2xl p-2 w-full"}`}>
        <Logo co={current} size={collapsed ? 36 : 30} />
        {!collapsed && <><span className="font-cabinet font-semibold text-sm truncate flex-1 text-left text-white">{current ? current.name : "Toutes"}</span><ChevronDown size={15} className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} /></>}
      </button>
      {open && (
        <div data-testid="company-switcher-menu" className="absolute left-0 top-full mt-2 z-40 w-72 bg-card border border-white/10 rounded-2xl shadow-2xl p-2 animate-in fade-in">
          <p className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500">Espace de travail</p>
          <button data-testid="switch-all" onClick={() => { select("all"); setOpen(false); }} className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-white/[0.06] ${selectedId === "all" ? "bg-white/[0.06]" : ""}`}>
            <Logo co={null} size={28} /><span className="flex-1 text-sm font-medium">Toutes les entreprises</span>
            {allPending > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-sky-400 text-white text-[10px] font-bold flex items-center justify-center">{allPending > 9 ? "9+" : allPending}</span>}
            {selectedId === "all" && <Check size={16} className="text-klein" />}
          </button>
          <div className="max-h-64 overflow-y-auto">
            {managed.map((c) => {
              const p = perCompany?.[c.id]?.total || 0;
              return (
                <button key={c.id} data-testid={`switch-${c.id}`} onClick={() => { select(c.id); setOpen(false); }} className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-white/[0.06] ${selectedId === c.id ? "bg-white/[0.06]" : ""}`}>
                  <Logo co={c} size={28} /><span className="flex-1 text-sm font-medium truncate">{c.name}</span>
                  {p > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-sky-400 text-white text-[10px] font-bold flex items-center justify-center">{p > 9 ? "9+" : p}</span>}
                  {selectedId === c.id && <Check size={16} className="text-klein" />}
                </button>
              );
            })}
          </div>
          <div className="h-px bg-white/10 my-1.5" />
          <button data-testid="switcher-new" onClick={() => { navigate("/companies"); setOpen(false); }} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-white/[0.06] text-sm text-klein"><Plus size={16} /> Nouvelle entreprise</button>
        </div>
      )}
    </div>
  );
}

function NavItem({ n, collapsed, count, onNavigate, locked, onLocked }) {
  const Icon = n.icon;
  if (locked) {
    return (
      <button data-testid={`${n.testid}-locked`} onClick={onLocked} title={`${n.label} — Passer à une licence supérieure`}
        className={`group relative flex items-center gap-3 rounded-full transition-colors w-full ${collapsed ? "justify-center w-11 h-11 mx-auto" : "h-10 mx-2 px-3.5"} text-zinc-600 hover:bg-white/[0.04] cursor-pointer`}>
        <Icon size={18} className="shrink-0 opacity-60" />
        {!collapsed && <span className="text-sm flex-1 truncate text-left opacity-70">{n.label}</span>}
        {!collapsed && <Lock size={13} className="shrink-0 text-amber-400/70" />}
      </button>
    );
  }
  return (
    <NavLink to={n.to} end={n.end} data-testid={n.testid} onClick={onNavigate}
      className={({ isActive }) => `group relative flex items-center gap-3 rounded-full transition-colors ${collapsed ? "justify-center w-11 h-11 mx-auto" : "h-10 mx-2 px-3.5"} ${isActive ? "bg-sky-400/15 text-sky-400 font-semibold" : "text-zinc-400 hover:bg-foreground/[0.06] hover:text-foreground"}`}>
      {({ isActive }) => (
        <>
          {isActive && !collapsed && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-sky-400" />}
          <Icon size={18} className="shrink-0" />
          {!collapsed && <span className="text-sm flex-1 truncate">{n.label}</span>}
          {count ? <span className={`${collapsed ? "absolute -top-1 -right-1" : ""} min-w-[20px] h-5 px-1.5 rounded-full bg-sky-400 text-white text-[11px] font-cabinet font-bold tabular-nums leading-none flex items-center justify-center shadow-[0_2px_6px_rgba(56,189,248,0.5)] ring-1 ring-white/25`}>{count > 99 ? "99+" : count}</span> : null}
        </>
      )}
    </NavLink>
  );
}

function Greeting() {
  const { user } = useAuth();
  const first = (user?.name || "").trim().split(" ")[0] || "";
  const h = new Date().getHours();
  let hello = "Bonjour", wish = "bonne journée", Icon = Sun;
  if (h < 12) { hello = "Bonjour"; wish = "bonne matinée"; Icon = Sunrise; }
  else if (h < 18) { hello = "Bon après-midi"; wish = "bel après-midi"; Icon = Sun; }
  else { hello = "Bonsoir"; wish = "bonne soirée"; Icon = Moon; }
  return (
    <div data-testid="header-greeting" className="hidden md:flex flex-col items-end leading-tight mr-1 shrink-0">
      <span className="text-sm font-semibold text-white">{hello}{first ? `, ${first}` : ""} <Icon size={13} className="inline -mt-0.5 text-sky-300" /></span>
      <span className="text-[11px] text-zinc-300">Passez une {wish}</span>
    </div>
  );
}

function ThemeToggle() {
  const [light, setLight] = useState(() => typeof document !== "undefined" && document.documentElement.classList.contains("light"));
  const toggle = () => { const next = !light; setLight(next); document.documentElement.classList.toggle("light", next); localStorage.setItem("fd_theme", next ? "light" : "dark"); };
  return <button data-testid="theme-toggle" onClick={toggle} title={light ? "Mode sombre" : "Mode clair"} className="p-2.5 rounded-full text-zinc-400 hover:text-foreground hover:bg-foreground/[0.06] transition-colors shrink-0">{light ? <Moon size={18} /> : <Sun size={18} />}</button>;
}

function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useOutside(() => setOpen(false));
  return (
    <div ref={ref} className="relative shrink-0">
      <button data-testid="profile-menu-btn" onClick={() => setOpen((o) => !o)} title="Profil & réglages" className="rounded-full ring-2 ring-transparent hover:ring-klein/40 transition-all">
        <Avatar name={user?.name} color={user?.avatar_color} size={36} src={user?.avatar_url} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-40 w-60 bg-card border border-black/10 dark:border-white/10 rounded-2xl shadow-2xl p-2 animate-in fade-in">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <Avatar name={user?.name} color={user?.avatar_color} size={40} src={user?.avatar_url} />
            <div className="min-w-0"><div className="text-sm font-semibold truncate">{user?.name}</div><div className="text-xs text-zinc-500 truncate">{user?.email}</div></div>
          </div>
          <div className="h-px bg-black/10 dark:bg-white/10 my-1" />
          <button data-testid="settings-profile" onClick={() => { navigate("/profile"); setOpen(false); }} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.06] text-sm text-left"><UserCircle size={16} className="text-zinc-400" /> Mon profil</button>
          <button data-testid="settings-companies" onClick={() => { navigate("/companies"); setOpen(false); }} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.06] text-sm text-left"><Building2 size={16} className="text-zinc-400" /> Entreprises & réglages</button>
          <div className="h-px bg-black/10 dark:bg-white/10 my-1" />
          <button data-testid="logout-button" onClick={logout} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-signal/10 text-sm text-left text-zinc-500 hover:text-signal"><LogOut size={16} /> Déconnexion</button>
        </div>
      )}
    </div>
  );
}

export function Layout({ children }) {
  const { managed, selectedId, selectedCompany } = useCompany();
  const { user } = useAuth();
  const { summary } = useRealtime();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("fd_sidebar") === "collapsed");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [planMods, setPlanMods] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (user && user.role !== "client" && !localStorage.getItem("fd_tour_done")) {
      const t = setTimeout(() => setTourOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [user]);

  useEffect(() => {
    const h = () => setTourOpen(true);
    window.addEventListener("fd:start-tour", h);
    return () => window.removeEventListener("fd:start-tour", h);
  }, []);

  useEffect(() => {
    api.get("/license-plans").then(({ data }) => {
      const map = {};
      (data.plans || []).forEach((p) => { map[p.key] = p.modules || []; });
      setPlanMods(map);
    }).catch(() => {});
  }, []);

  const toggleCollapse = () => setCollapsed((c) => { localStorage.setItem("fd_sidebar", c ? "expanded" : "collapsed"); return !c; });
  const isAdmin = user?.role === "admin";
  const isAll = selectedId === "all";
  const planKey = isAll ? "entreprise" : (selectedCompany?.plan || "entreprise");
  const allowedMods = planMods ? (planMods[planKey] || planMods["entreprise"] || []) : null;
  const isAllowed = (mod) => !mod || isAdmin || (allowedMods === null) || allowedMods.includes(mod);
  const onLocked = () => { toast.info("Cette fonctionnalité nécessite une licence supérieure. Gérez la licence dans Entreprises."); navigate("/companies"); };

  const renderItem = (n, close) => {
    if (n.company && isAll) return null; // modules facturation : seulement entreprise sélectionnée
    const locked = !isAllowed(n.mod);
    return <NavItem key={n.to} n={n} collapsed={collapsed} count={summary[n.badge]} onNavigate={close} locked={locked} onLocked={onLocked} />;
  };

  const billingLocked = !isAdmin && allowedMods && !allowedMods.includes("devis");
  const navItems = (close) => (
    <>
      <div className="space-y-0.5">{PRIMARY.map((n) => renderItem(n, close))}</div>
      {!isAll && (
        <>
          <div className={`my-3 ${collapsed ? "mx-3" : "mx-4"} h-px bg-white/[0.08]`} />
          {!collapsed && <p className="px-4 mb-1 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">Facturation {billingLocked && <Lock size={10} className="text-amber-400/70" />}</p>}
          <div className="space-y-0.5">{BILLING.map((n) => renderItem(n, close))}</div>
        </>
      )}
      <div className={`my-3 ${collapsed ? "mx-3" : "mx-4"} h-px bg-white/[0.08]`} />
      <div className="space-y-0.5">{SECONDARY.map((n) => renderItem(n, close))}</div>
      {isAdmin && <div className="mt-1"><NavItem n={{ to: "/admin", label: "Administration", icon: Settings, testid: "nav-admin", mod: null }} collapsed={collapsed} onNavigate={close} /></div>}
      {managed.length === 0 && !isAdmin && <div className="mt-1"><NavItem n={{ to: "/portal", label: "Espace client", icon: UserCircle, testid: "nav-portal", mod: null }} collapsed={collapsed} onNavigate={close} /></div>}
    </>
  );

  const SidebarInner = ({ mobile }) => (
    <div className={`h-full flex flex-col py-3 ${mobile ? "fd-rail-bg" : ""} ${collapsed && !mobile ? "w-[72px]" : "w-[256px]"} transition-all duration-300`}>
      <div className={`mb-3 ${collapsed && !mobile ? "px-2" : "px-3"}`}><CompanySwitcher collapsed={collapsed && !mobile} /></div>
      <nav className="flex-1 overflow-y-auto no-scrollbar">{navItems(() => setMobileOpen(false))}</nav>
    </div>
  );

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden fd-rail-bg">
      {/* Header — même fond navy que les rails, fondu */}
      <header className="h-16 shrink-0 relative z-30">
        <div className="h-full flex items-center gap-3 px-3 sm:px-4">
          <button data-testid="sidebar-toggle-btn" onClick={() => { window.innerWidth < 1024 ? setMobileOpen(true) : toggleCollapse(); }} className="p-2.5 rounded-full text-zinc-400 hover:text-foreground hover:bg-foreground/[0.06] transition-colors shrink-0"><Menu size={20} /></button>
          <button data-testid="logo-home" onClick={() => navigate("/")} title="Accueil — FlowDesk" className="flex items-center gap-0 shrink-0 hover:opacity-90 transition-opacity">
            <img src="/brand/flowdesk-mark.png" alt="FlowDesk" className="w-14 h-14 object-contain drop-shadow-[0_2px_14px_rgba(79,156,255,0.5)]" />
            <span className="hidden sm:inline font-cabinet font-black text-lg text-white tracking-tight -ml-2">FlowDesk</span>
          </button>
          <GlobalSearch />
          <RunningTimers />
          <InstallAppButton compact />
          <button data-testid="tour-help-btn" onClick={() => setTourOpen(true)} title="Aide — Revoir la visite guidée" className="p-2.5 rounded-full text-zinc-400 hover:text-foreground hover:bg-foreground/[0.06] transition-colors shrink-0"><HelpCircle size={18} /></button>
          <ThemeToggle />
          <Greeting />
          <ProfileMenu />
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex min-h-0">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex shrink-0"><SidebarInner mobile={false} /></aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-sidebar">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <div className="relative animate-in slide-in-from-left"><SidebarInner mobile /></div>
          </div>
        )}

        {/* Main floating card */}
        <main data-testid="main-content-card" className="flex-1 min-w-0 min-h-0 overflow-hidden lg:my-2 lg:mx-2 lg:rounded-2xl border border-black/5 dark:border-white/[0.06] bg-background shadow-[0_8px_30px_rgba(0,0,0,0.18)] relative z-10">{children}</main>

        <RightRail />
      </div>
      <StartDayPrompt />
      {tourOpen && <GuidedTour onClose={() => setTourOpen(false)} />}
    </div>
  );
}
